export const QuestionType = {
	Option: 1,
	Date: 2,
	Time: 3,
	// DateTime: 4,
	Text: 5,
	Number: 6,
	File: 7,
	Boolean: 8,
} as const;

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

export const QuestionTypeLabels: Record<QuestionType, string> = {
	[QuestionType.Option]: "Seleção",
	[QuestionType.Date]: "Data",
	[QuestionType.Time]: "Hora",
	// [QuestionType.DateTime]: 'Data e Hora',
	[QuestionType.Text]: "Texto",
	[QuestionType.Number]: "Número",
	[QuestionType.File]: "Foto",
	[QuestionType.Boolean]: "Sim/Não",
};

export type QuestionOption = {
	id: number;
	questionSettingsId?: number;
	label: string;
	position: number;
	isDefault?: boolean;
	isDamage?: boolean;
};

export type Question = {
	id: number;
	identifier: string | null;
	prompt: string;
	description?: string | null;
	/** Only meaningful in template context (TemplateQuestion). */
	isRequired?: boolean;
	type: QuestionType;
	settings?: QuestionSettings;
};

export const QUESTION_DESCRIPTION_MAX_LENGTH = 200;

// Tipo de restrição de data
export const DateRangeType = {
	Any: 0, // Aceita qualquer data, sem restrições
	PastOnly: 1, // Permite apenas datas no passado (≤ hoje)
	FutureOnly: 2, // Permite apenas datas no futuro (≥ hoje)
	Range: 3, // Restringe a resposta a um intervalo definido entre MinDate e MaxDate
} as const;

export type DateRangeType = (typeof DateRangeType)[keyof typeof DateRangeType];

// Tipo de exibição de opções
export const QuestionOptionDisplayType = {
	Icon: 1, // Exibidas como ícones visuais (Regular, Avariado, Não Possui)
	Checkbox: 2, // Exibidas como caixas de seleção, permitindo múltiplas escolhas
	Radio: 3, // Seleção única (Sim/Não, etc)
	ComboBox: 4, // Exibidas em um menu suspenso (dropdown)
	List: 5, // Exibidas em uma lista simples
} as const;

export type QuestionOptionDisplayType =
	(typeof QuestionOptionDisplayType)[keyof typeof QuestionOptionDisplayType];

// Configurações específicas por tipo de questão
export type QuestionSettings = {
	// Text
	minLength?: number; // Tamanho mínimo permitido para a resposta em texto
	maxLength?: number; // Tamanho máximo permitido para a resposta em texto

	// Number
	minValue?: number; // Valor numérico mínimo aceito
	maxValue?: number; // Valor numérico máximo aceito

	// Date / DateTime
	dateRangeType?: DateRangeType; // Tipo de restrição de data
	minDate?: Date; // Data mínima permitida
	maxDate?: Date; // Data máxima permitida

	// File
	maxFiles?: number; // Quantidade máxima de arquivos que podem ser anexados
	maxFileSizeMB?: number; // Tamanho máximo de cada arquivo em MB

	// Option (múltipla escolha)
	optionDisplayType?: QuestionOptionDisplayType; // Define como as opções serão exibidas
	isMultipleSelection?: boolean; // Indica se é permitido selecionar mais de uma opção
	questionOptions?: QuestionOption[]; // Lista de opções disponíveis para a questão
};

// verifica se um tipo de questão precisa de opções
export function questionTypeNeedsOptions(type: QuestionType): boolean {
	return type === QuestionType.Option || type === QuestionType.Boolean;
}

// verifica se um tipo de questão aceita múltiplas respostas
export function questionTypeAllowsMultiple(type: QuestionType): boolean {
	return type === QuestionType.Option;
}

// obter o tipo de input HTML baseado no QuestionType
export function getInputType(type: QuestionType): string {
	switch (type) {
		case QuestionType.Date:
			return "date";
		case QuestionType.Time:
			return "time";
		case QuestionType.Number:
			return "number";
		case QuestionType.File:
			return "file";
		case QuestionType.Text:
			return "text";
		default:
			return "text";
	}
}

// obter configurações padrão por tipo de questão
export function getDefaultSettings(type: QuestionType): QuestionSettings {
	switch (type) {
		case QuestionType.Text:
			return {
				minLength: 0,
				maxLength: 500,
			};
		case QuestionType.Number:
			return {
				minValue: undefined,
				maxValue: undefined,
			};
		case QuestionType.Date:
		// case QuestionType.DateTime:
		//     return {
		//         dateRangeType: DateRangeType.Any,
		//         minDate: undefined,
		//         maxDate: undefined
		//     };
		case QuestionType.File:
			return {
				maxFiles: 5,
				maxFileSizeMB: 10,
			};
		case QuestionType.Option:
			return {
				optionDisplayType: QuestionOptionDisplayType.Checkbox,
				isMultipleSelection: false,
				questionOptions: [],
			};
		case QuestionType.Boolean:
			return {
				optionDisplayType: QuestionOptionDisplayType.Radio,
				isMultipleSelection: false,
				questionOptions: createYesNoOptions(),
			};
		default:
			return {};
	}
}

// validar se um valor está dentro das configurações
export function validateQuestionValue(
	value: any,
	type: QuestionType,
	settings?: QuestionSettings
): { valid: boolean; error?: string } {
	if (!settings) {
		return { valid: true };
	}

	switch (type) {
		case QuestionType.Text: {
			const textValue = value as string;
			if (settings.minLength && textValue.length < settings.minLength) {
				return {
					valid: false,
					error: `Texto deve ter no mínimo ${settings.minLength} caracteres`,
				};
			}
			if (settings.maxLength && textValue.length > settings.maxLength) {
				return {
					valid: false,
					error: `Texto deve ter no máximo ${settings.maxLength} caracteres`,
				};
			}
			break;
		}

		case QuestionType.Number: {
			const numValue = value as number;
			if (settings.minValue !== undefined && numValue < settings.minValue) {
				return {
					valid: false,
					error: `Valor deve ser no mínimo ${settings.minValue}`,
				};
			}
			if (settings.maxValue !== undefined && numValue > settings.maxValue) {
				return {
					valid: false,
					error: `Valor deve ser no máximo ${settings.maxValue}`,
				};
			}
			break;
		}

		case QuestionType.Date: {
			const dateValue = new Date(value);
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			if (
				settings.dateRangeType === DateRangeType.PastOnly &&
				dateValue > today
			) {
				return {
					valid: false,
					error: "Apenas datas no passado são permitidas",
				};
			}
			if (
				settings.dateRangeType === DateRangeType.FutureOnly &&
				dateValue < today
			) {
				return { valid: false, error: "Apenas datas no futuro são permitidas" };
			}
			if (settings.dateRangeType === DateRangeType.Range) {
				if (settings.minDate && dateValue < new Date(settings.minDate)) {
					return {
						valid: false,
						error: `Data deve ser posterior a ${settings.minDate.toLocaleDateString()}`,
					};
				}
				if (settings.maxDate && dateValue > new Date(settings.maxDate)) {
					return {
						valid: false,
						error: `Data deve ser anterior a ${settings.maxDate.toLocaleDateString()}`,
					};
				}
			}
			break;
		}

		case QuestionType.File: {
			const files = value as FileList | File[];
			const maxFiles = settings.maxFiles ?? 5;
			if (files.length > maxFiles) {
				return {
					valid: false,
					error: `Máximo de ${maxFiles} arquivos permitido`,
				};
			}
			// Tamanho máximo por arquivo fixo em 10 MB quando não informado
			const maxFileSizeMB = settings.maxFileSizeMB ?? 10;
			const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
			for (const file of Array.from(files)) {
				if (file.size > maxSizeBytes) {
					return {
						valid: false,
						error: `Foto ${file.name} excede o tamanho máximo de ${maxFileSizeMB}MB`,
					};
				}
			}
			break;
		}

		default:
			return { valid: true };
	}

	return { valid: true };
}

// validar opções de uma questão
export function validateQuestionOptions(options: QuestionOption[]): {
	valid: boolean;
	errors: string[];
} {
	const errors: string[] = [];

	// Verifica se há labels duplicados
	const labels = options.map((opt) => opt.label.toLowerCase());
	const duplicateLabels = labels.filter(
		(label, index) => labels.indexOf(label) !== index
	);
	if (duplicateLabels.length > 0) {
		errors.push("Existem opções com labels duplicados");
	}

	// Verifica se há apenas uma opção padrão
	const defaultOptions = options.filter((opt) => opt.isDefault);
	if (defaultOptions.length > 1) {
		errors.push("Apenas uma opção pode ser marcada como padrão");
	}

	// Verifica se as posições são sequenciais
	const positions = options.map((opt) => opt.position).sort((a, b) => a - b);
	for (let i = 0; i < positions.length; i++) {
		if (positions[i] !== i + 1) {
			errors.push("As posições das opções devem ser sequenciais (1, 2, 3...)");
			break;
		}
	}

	// Verifica se há labels vazios
	if (options.some((opt) => !opt.label || opt.label.trim() === "")) {
		errors.push("Todas as opções devem ter um label preenchido");
	}

	return {
		valid: errors.length === 0,
		errors,
	};
}

// reordenar opções após uma alteração
export function reorderOptions(options: QuestionOption[]): QuestionOption[] {
	return options.map((option, index) => ({
		...option,
		position: index + 1,
	}));
}

// criar opções padrão de Sim/Não
export function createYesNoOptions(): QuestionOption[] {
	return [
		{ id: 1, label: "Sim", position: 1, isDefault: false, isDamage: false },
		{ id: 2, label: "Não", position: 2, isDefault: false, isDamage: false },
	];
}

// criar opções padrão de checklist de vistoria
export function createInspectionOptions(): QuestionOption[] {
	return [
		{ id: 1, label: "Regular", position: 1, isDefault: false, isDamage: false },
		{ id: 2, label: "Avariado", position: 2, isDefault: false, isDamage: true },
		{
			id: 3,
			label: "Não Possui",
			position: 3,
			isDefault: false,
			isDamage: false,
		},
	];
}
